import Paper from '@/components/Paper'
import { FilterBar } from '@/components/ui/data-table-customized/filter-bar/filter-bar'
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import { DEPARTMENT_IDS } from '@/constants/departments'
import {
    getEmployeeContains,
    getEmployeesOptions,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { SalesTargetFilters } from '../../types'

const EMPLOYEE_DEPT_IDS = [DEPARTMENT_IDS.ACCOUNT, DEPARTMENT_IDS.SALES]

const salePaginatedOptions = (query: string, page?: number) =>
    getEmployeesOptionsPaginated(query, EMPLOYEE_DEPT_IDS, 'active', page)

interface FiltersProps {
    control: UseFormReturn<SalesTargetFilters>['control']
    setValue: UseFormReturn<SalesTargetFilters>['setValue']
    hideDepartmentSelect?: boolean
    hideEmployeeSelect?: boolean
    hideProductGroupSelect?: boolean
    disabledEmployeeSelect?: boolean
}

const Filters: React.FC<FiltersProps> = ({
    control,
    setValue,
    hideDepartmentSelect,
    hideEmployeeSelect,
    hideProductGroupSelect,
    disabledEmployeeSelect,
}) => {
    const setFilter = (key: keyof SalesTargetFilters, value: any) => {
        setValue(key, value)
    }

    const filterItems: FilterItemConfig[] = useMemo(
        () => [
            {
                name: 'department_ids',
                type: 'department' as const,
                label: 'Phòng ban',
                enabled: !hideDepartmentSelect,
                onChange: async (value: any) => {
                    setValue('department_ids', value)
                    if (!value || value?.length === 0) {
                        setValue('employee_id', [], { shouldDirty: true })
                        return
                    }
                    const employees = await getEmployeesOptions(
                        '',
                        value,
                        'active'
                    )
                    setValue(
                        'employee_id',
                        employees?.map((emp) => emp.value as number) ?? [],
                        { shouldDirty: true }
                    )
                },
            },
            {
                name: 'employee_id',
                type: 'async-select' as const,
                label: 'Nhân viên',
                placeholder: 'Chọn nhân viên',
                asyncOptions: salePaginatedOptions,
                getDefaultOptions: (values: number[]) =>
                    getEmployeeContains({ employee_ids: values }),
                multiple: true,
                cols: 2,
                enabled: !hideEmployeeSelect,
                className: disabledEmployeeSelect
                    ? 'pointer-events-none opacity-60'
                    : '',
            },
            {
                name: 'date_range',
                type: 'daterangepicker' as const,
                names: ['gte', 'lte'] as [string, string],
                label: 'Khoảng thời gian',
                showPresets: true,
                numberOfMonths: 2,
            },
            {
                name: 'product_group_name',
                type: 'async-select' as const,
                label: 'Nhóm sản phẩm',
                placeholder: 'Chọn nhóm sản phẩm',
                asyncOptions: getProductsGroupOptions,
                multiple: false,
                enabled: !hideProductGroupSelect,
            },
        ],
        [
            hideDepartmentSelect,
            hideEmployeeSelect,
            hideProductGroupSelect,
            disabledEmployeeSelect,
            setValue,
        ]
    )

    return (
        <Paper className="py-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
                tourPrefix="target-filter"
            />
        </Paper>
    )
}

export default Filters
